import React, { useState } from 'react';
import { Snackbar } from '@shared/components/Snackbar';
import { SHARED_MESSAGES } from '@/shared/constants/messages';
import { VIEW_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import { SERVICE_MANAGEMENT_LABELS as TL } from '../../constants/translations/labels';
import type { ViewServiceRequestDetail } from '../../types/viewServiceRequestType';
import { FileRow, SectionCard } from './viewServiceRequestUI';
import { downloadFile } from './attachmentFile';

interface SubLabelProps {
  text: string;
}

const SubLabel: React.FC<SubLabelProps> = ({ text }) => (
  <p className="text-bodySmall font-normal text-on-surface-variant">{text}</p>
);

interface SRResponseTabProps {
  detail: ViewServiceRequestDetail | undefined;
}

/**
 * Completion "response" tab — the assigned admin's uploaded documents and
 * free-text note, read back from `detail.response`. Only rendered when the
 * request is completed (the page gates the tab on status; see
 * `ViewServiceRequest`).
 */
const SRResponseTab: React.FC<SRResponseTabProps> = ({ detail }) => {
  // Drop attachments whose presigned URL could not be generated (null url).
  const attachments = (detail?.response?.attachments ?? []).filter((a) =>
    Boolean(a.url)
  );
  const notes = detail?.response?.notes?.trim() ?? '';
  const [showDownloadSnackbar, setShowDownloadSnackbar] = useState(false);

  const handleDownload = (url: string, filename: string): void => {
    setShowDownloadSnackbar(true);
    downloadFile(url, filename);
  };

  return (
    <>
      <SectionCard title={L.NAV_RESPONSE} testId="view-sr-section-response">
        <div className="flex flex-col gap-3">
          <SubLabel text={TL.SECTION_RESPONSE_ATTACHMENTS.polish} />
          {attachments.length > 0 ? (
            <div className="flex flex-col gap-4">
              {attachments.map((file, idx) => (
                <FileRow
                  key={`${file.filename}-${idx}`}
                  file={file}
                  testId={`view-sr-response-attachment-${idx}`}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          ) : (
            <p
              className="text-sm text-[#181A1B]"
              data-testid="view-sr-response-attachments-empty"
            >
              {L.EMPTY_CELL}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <SubLabel text={L.SECTION_RESPONSE_NOTES} />
          <p
            className="text-sm text-[#181A1B] whitespace-pre-wrap break-words"
            data-testid="view-sr-response-notes-value"
          >
            {notes.length > 0 ? notes : L.EMPTY_CELL}
          </p>
        </div>
      </SectionCard>

      <Snackbar
        open={showDownloadSnackbar}
        message={SHARED_MESSAGES.DOWNLOAD_READY_MESSAGE}
        variant="success"
        onClose={() => setShowDownloadSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};

export default SRResponseTab;
